/**
 * Nexus Imports
 */
import { Dispatch, FC, SetStateAction, useState, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';

/**
 * Internal Imports
 */
import { Dropdown } from '../../shared/dropdowns';

import { RootState } from '../../../redux/store';
import {
  getQuestionBank,
  setQuestionsOfTemplate
} from '../../../redux/actions';
import { useGetMasterTemplatesQuery } from '../../../redux/apis/Templates.api';

/**
 * Global Type Definition Imports
 */
import {
  ICondensedMasterTemplate,
  IFullTemplate,
  Question
} from '../../../config/interfaces';

/**
 * Style Imports
 */
import {
  Container,
  SelectTemplateHeader
} from './SelectTemplatesDropdown.styles';

interface SelectTemplateDropDownProps {
  allQuestions: Question[];
  roundingType?: string;
  selectedMasterTemplate?: ICondensedMasterTemplate | IFullTemplate | null;
  setSelectedMasterTemplate?: Dispatch<
    SetStateAction<ICondensedMasterTemplate | IFullTemplate | null>
  >;
  type: string;
}

const SelectTemplateDropDown: FC<SelectTemplateDropDownProps> = ({
  allQuestions,
  roundingType,
  selectedMasterTemplate,
  setSelectedMasterTemplate = () => {},
  type
}) => {
  const dispatch = useDispatch();

  const [selectedTemplateName, setSelectedTemplateName] = useState<string>(
    type === 'questionBank' ? 'All' : ''
  );
  const [filteredMasterTemplateList, setFilteredMasterTemplateList] = useState<
    (ICondensedMasterTemplate | IFullTemplate)[]
  >([]);
  const [rendered, setRendered] = useState<boolean>(false);

  const [open, setOpen] = useState(false);

  const {
    data: masterTemplateData
    // isError: isGetMasterTemplatesError,
    // isFetching: isGetMasterTemplatesFetching,
    // isLoading: isGetMasterTemplatesLoading
  } = useGetMasterTemplatesQuery(
    { archived: false, condensed: false },
    {
      refetchOnMountOrArgChange: 15
    }
  );

  const onChangeTemplate = (value: string) => {
    switch (type) {
      case 'questionBank': {
        if (value === 'all') {
          setSelectedTemplateName('All');
          dispatch(setQuestionsOfTemplate(allQuestions));
        } else {
          const newSelectedTemplate = filteredMasterTemplateList.find(
            (template: ICondensedMasterTemplate | IFullTemplate) =>
              template.templateConfigId === value
          );
          setSelectedTemplateName(newSelectedTemplate?.name ?? '');
          const questionsToList =
            newSelectedTemplate?.questions.reduce(
              (
                acc: Question[],
                selectedQuestion: {
                  hidden: string;
                  id: string;
                  templateLevel: string;
                }
              ) => {
                const foundQuestion = allQuestions.find(
                  (allQuestion: Question) =>
                    allQuestion.questionId === selectedQuestion.id
                );
                return foundQuestion ? [...acc, foundQuestion] : [...acc];
              },
              []
            ) ?? [];

          dispatch(setQuestionsOfTemplate(questionsToList));
        }
        break;
      }
      case 'templateTree': {
        const newSelectedTemplate = filteredMasterTemplateList.find(
          (template: ICondensedMasterTemplate | IFullTemplate) =>
            template.templateConfigId === value
        );
        if (newSelectedTemplate) setSelectedMasterTemplate(newSelectedTemplate);
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    switch (type) {
      case 'questionBank':
        dispatch(getQuestionBank());
        break;
      default:
        break;
    }
    setRendered(true);
  }, []);

  useEffect(() => {
    if (type === 'templateTree' && rendered) setSelectedMasterTemplate(null);
  }, [roundingType]);

  useEffect(() => {
    if (roundingType) {
      const isSupportRoundingType = !(
        roundingType === 'patient' ||
        roundingType === 'employee' ||
        roundingType === 'aidetValidation' ||
        roundingType === 'bssrValidation' ||
        roundingType === 'validation'
      );
      const filtered = isSupportRoundingType
        ? masterTemplateData?.filter(
            (template: ICondensedMasterTemplate | IFullTemplate) =>
              template.roundingType === 'csrn' ||
              template.roundingType === 'cned'
          ) ?? []
        : masterTemplateData?.filter(
            (template: ICondensedMasterTemplate | IFullTemplate) =>
              template.roundingType === roundingType
          ) ?? [];
      setFilteredMasterTemplateList(filtered);
    } else {
      setFilteredMasterTemplateList(masterTemplateData ?? []);
    }

    switch (type) {
      case 'questionBank':
        dispatch(setQuestionsOfTemplate(allQuestions));
        break;
      default:
        console.log('type not recognized');
    }
  }, [masterTemplateData, allQuestions, roundingType]);

  useEffect(() => {
    switch (type) {
      case 'templateTree': {
        if (filteredMasterTemplateList?.length > 0) {
          if (!selectedMasterTemplate) {
            setSelectedTemplateName(filteredMasterTemplateList[0]?.name || '');
            setSelectedMasterTemplate(filteredMasterTemplateList[0]);
          } else {
            const newlySelectedTemplate = filteredMasterTemplateList.find(
              (template: ICondensedMasterTemplate | IFullTemplate) =>
                template.templateConfigId ===
                selectedMasterTemplate.templateConfigId
            );
            setSelectedTemplateName(newlySelectedTemplate?.name ?? '');
          }
        }
        break;
      }
      default: {
        console.log('type not recognized');
      }
    }
  }, [filteredMasterTemplateList, selectedMasterTemplate]);

  return (
    <Container>
      <SelectTemplateHeader className="mb-0 pe-3">
        Template
      </SelectTemplateHeader>
      <Dropdown
        classes="ps-0"
        open={open}
        placeholder={selectedTemplateName || ''}
        setOpen={setOpen}
      >
        {type === 'questionBank' ? (
          <div
            key="templates-all"
            aria-selected={selectedTemplateName === 'all'}
            className={`dropdown-hover-item${
              selectedTemplateName === 'all' ? ' selected' : ''
            }`}
            role="option"
            tabIndex={0}
            onClick={() => onChangeTemplate('all')}
            onKeyDown={() => onChangeTemplate('all')}
          >
            All
          </div>
        ) : null}
        {filteredMasterTemplateList.map(
          (template: ICondensedMasterTemplate | IFullTemplate) => {
            return (
              <div
                key={template.templateConfigId}
                aria-selected={selectedTemplateName === template.name}
                className={`dropdown-hover-item${
                  selectedTemplateName === template.name ? ' selected' : ''
                }`}
                role="option"
                tabIndex={0}
                onClick={() => onChangeTemplate(template.templateConfigId)}
                onKeyDown={() => onChangeTemplate(template.templateConfigId)}
              >
                {template.name}
              </div>
            );
          }
        )}
      </Dropdown>
    </Container>
  );
};

const mapReduxStateToProps = (state: RootState) => ({
  allQuestions: state.TemplateReducer.allQuestions
});

export default connect(mapReduxStateToProps)(SelectTemplateDropDown);
